<template>
  <v-card>
    <v-card-title>
      <div class="heading">Задание {{ task.name }}</div>
    </v-card-title>
    <v-card-title v-if="$route.params.id">
      <v-btn
        outlined
        :to="{ name: 'activist_taken_task_list', params: { task_id: $route.params.id } }"
        >Смотреть взятые задачи</v-btn
      >
    </v-card-title>
    <v-card-text>
      <v-col lg12>
        <v-form ref="taskForm" name="taskForm">
          <v-col cols="12">
            <FileInput
              @file-selected="imageSelected"
              label="Обложка"
              :field-name="'image'"
              :url="task.image.url"
            ></FileInput>
          </v-col>
          <v-col lg12>
            <v-text-field v-model="task.name" :rules="rules.required" name="name" label="Название">
            </v-text-field>
          </v-col>
          <v-col lg12>
            <ckeditor
              :editor="ckeditor"
              v-model="task.description"
              :config="ckConfig"
              type="classic"
              :upload-adapter="UploadAdapter"
              @ready="setUploader"
            >
            </ckeditor>
            <input type="text" name="description" :value="task.description" hidden />
          </v-col>
          <v-col lg12>
            <v-select
              :items="taskKindSelect"
              v-model="task.kind"
              name="kind"
              label="Тип задания"
            ></v-select>
            <v-select
              v-show="isOnline"
              :items="socialKindSelect"
              v-model="task.social_kind"
              name="social_kind"
              label="Тип социальной сети"
            ></v-select>
            <v-text-field
              v-show="isOnline"
              v-model="task.url"
              name="url"
              label="Ссылка на пост"
            ></v-text-field>
            <input v-if="isOnlineVK" type="number" name="group_id" :value="task.group_id" hidden />
            <input v-if="isOnlineVK" type="number" name="post_id" :value="task.post_id" hidden />
            <v-text-field
              v-show="isOnlineVK"
              v-model="task.hash"
              name="hash"
              label="Hash"
              placeholder="Получить hash можно только в конструкторе виджета или в окне «Поделиться» для выбранной записи."
            ></v-text-field>
            <v-select
              v-show="isOnline"
              :items="goalsKindSelect"
              v-model="task.goals"
              multiple
              label="Цели"
            ></v-select>
            <v-select
              :items="taskSubKindSelect"
              v-model="task.sub_kind"
              name="sub_kind"
              label="Вид актуальности"
            ></v-select>
            <v-col cols="3">
              <v-menu
                ref="dateMenu"
                v-model="deadlineMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                :return-value.sync="task.deadline"
                lazy
                transition="scale-transition"
                offset-y
                full-width
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="task.deadline"
                    prepend-icon="event"
                    readonly
                    v-on="on"
                    outlined
                    clearable
                  >
                    <template v-slot:label>
                      <span
                        >Срок выполнения <span v-if="dateLabel">до: </span> {{ dateLabel }}</span
                      >
                    </template>
                  </v-text-field>
                </template>
                <v-date-picker v-model="task.deadline" locale="ru-ru" no-title scrollable>
                  <v-spacer></v-spacer>
                  <v-btn text color="primary" @click="deadlineMenu = false">Закрыть</v-btn>
                  <v-btn text color="primary" @click="$refs.dateMenu.save(task.deadline)">
                    Выбрать
                  </v-btn>
                </v-date-picker>
              </v-menu>
              <input type="text" hidden name="deadline" :value="timestampedDeadline" />
            </v-col>
          </v-col>
          <v-col lg12>
            <div class="subtitle-1">Шаги</div>
            <v-col lg12 v-for="(step, i) of task.steps" :key="'step' + i">
              <TaskStep
                :step="step"
                @step-was-updated="stepWasUpdated"
                @step-was-deleted="stepWasDeleted"
              ></TaskStep>
            </v-col>
            <v-divider></v-divider>
            <v-col cols="12">
              <v-btn outlined rounded @click="addTaskStep" color="primary"
                >Добавить еще один шаг</v-btn
              >
            </v-col>
          </v-col>
        </v-form>
      </v-col>
    </v-card-text>
    <v-card-actions>
      <v-btn @click="save" color="green" dark>Сохранить</v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import {
  defaultTaskStep,
  dateToDeadline,
  deadlineToDate,
  getCreateTaskResponse,
  getCreateTaskStepResponse,
  getTaskResponse,
  getUpdateTaskResponse,
  goalsKindSelect,
  TaskKind,
  socialKindSelect,
  SocialKind,
} from '@/api/activist/task.ts'
import forms from '@/mixins/forms'
import { taskKindSelect, taskSubKindSelect } from '@/api/activist/task'
import { isEmpty } from 'lodash'
import TaskStep from '@/components/activist/TaskStep'
import FileInput from '@/components/FileInput'
import ckeditorMixin from '@/mixins/ckeditor'

export default {
  name: 'Task',
  components: { TaskStep, FileInput },
  mixins: [forms, ckeditorMixin],
  props: {
    taskId: { type: String, required: false, default: '' },
  },
  data() {
    return {
      deadlineMenu: false,
      task: {
        name: '',
        description: '',
        kind: '',
        sub_kind: '',
        scope: {},
        steps: [],
        deadline: null,
        image: {},
        social_kind: 0,
        goals: [],
        url: '',
        hash: '',
        group_id: 0,
        post_id: 0,
      },
    }
  },
  computed: {
    timestampedDeadline() {
      if (!this.task.deadline) {
        return ''
      }
      return dateToDeadline(this.task.deadline)
    },
    formattedDeadline() {
      if (!this.task.deadline) {
        return ''
      }
      return deadlineToDate(this.task.deadline)
    },
    taskKindSelect() {
      return taskKindSelect
    },
    taskSubKindSelect() {
      return taskSubKindSelect
    },
    socialKindSelect() {
      return socialKindSelect
    },
    goalsKindSelect() {
      return goalsKindSelect
    },
    dateLabel() {
      if (isEmpty(this.task.deadline)) {
        return ''
      }
      const date = this.$moment(this.task.deadline)
      return date.isValid() ? date.format('LL') : ''
    },
    isEdit() {
      return !!this.$route.params.id
    },
    isOnline() {
      return this.task.kind === TaskKind.Online
    },
    isOnlineVK() {
      return this.task.kind === TaskKind.Online && this.task.social_kind === SocialKind.VK
    },
  },
  watch: {
    'task.url'(v) {
      if (!v) {
        return
      }
      const parts = this.task.url.split('wall')
      if (parts.length < 2) {
        return
      }
      const groupPostParts = parts[1].split('_')
      if (groupPostParts.length < 2) {
        return
      }
      this.task.group_id = Number(groupPostParts[0])
      this.task.post_id = Number(groupPostParts[1])
    },
  },
  mounted() {
    if (this.isEdit) {
      this.getTaskData()
    }
  },
  methods: {
    save() {
      if (this.isEdit) {
        return this.updateTask()
      }
      return this.createTask()
    },
    getTaskData() {
      getTaskResponse(this.axios, this.$route.params.id)
        .then((resp) => {
          const task = resp.data
          task.deadline = deadlineToDate(resp.data.deadline)
          task.steps = (resp.data.steps ? resp.data.steps : []).map((x) => {
            x.deadline = deadlineToDate(x.deadline)
            return x
          })
          this.$set(this, 'task', task)
        })
        .catch((err) => {
          this.showSnackbar(err, 'error')
        })
    },
    updateTaskData() {},
    updateTask() {
      this.$log.debug('create update task request')
      this.task.deadline = dateToDeadline(this.task.deadline)
      const formData = new FormData(this.$refs.taskForm.$el)
      this.task.goals.forEach((x) => {
        formData.append('goals[]', x)
      })
      getUpdateTaskResponse(this.axios, this.task.id, formData)
        .then(async (resp) => {
          let steps = []
          for (const idx in this.task.steps) {
            let x = { ...this.task.steps[idx] }
            this.$log.debug(idx, x)
            if (isEmpty(x.id)) {
              if (isEmpty(x)) {
                continue
              }
              this.$log.debug('new', x)
              x.deadline = dateToDeadline(x.deadline)
              this.$log.debug('x.deadline', x.deadline)
              let resp = await getCreateTaskStepResponse(this.axios, this.task.id, x)
              x = { ...resp.data }
              x.deadline = deadlineToDate(x.deadline)
            }
            steps.push({ ...x })
            this.$log.debug('push', x)
          }

          const task = resp.data
          task.deadline = deadlineToDate(task.deadline)
          task.steps = [...steps]
          this.$set(this, 'task', { ...task })
          this.showSnackbar(this.messages.successSaved, 'success')
        })
        .catch((err) => {
          this.showSnackbar(err, 'error')
        })
    },
    createTask() {
      if (!this.$refs.taskForm.validate()) {
        this.showSnackbar('Исправьте ошибки', 'error')
        return
      }
      const formData = new FormData(this.$refs.taskForm.$el)
      this.task.goals.forEach((x) => {
        formData.append('goals[]', x)
      })
      getCreateTaskResponse(this.axios, formData)
        .then((resp) => {
          let steps = []
          if (this.task.steps) {
            steps = [...this.task.steps]
            steps.map(async (step) => {
              let stepResp
              try {
                this.$log.debug('new', step)
                step.deadline = dateToDeadline(step.deadline)
                this.$log.debug('step.deadline', step.deadline)
                stepResp = await getCreateTaskStepResponse(this.axios, resp.data.id, step)
              } catch (e) {
                this.showSnackbar(e, 'error')
                return step
              }
              step = stepResp.data
              step.deadline = deadlineToDate(step.deadline)
              this.showSnackbar(this.messages.successCreated + ' Шаг', 'success')
              return step
            })
          }
          const task = resp.data
          task.steps = steps.map((x) => {
            x.deadline = deadlineToDate(x.deadline)
            return x
          })
          task.deadline = deadlineToDate(task.deadline)
          this.$set(this, 'task', task)
          this.showSnackbar(this.messages.successCreated, 'success')
          this.$router.push({ name: 'activist_task_edit', params: { id: resp.data.id } })
        })
        .catch((err) => {
          this.showSnackbar(err, 'error')
        })
    },
    addTaskStep() {
      const step = { ...defaultTaskStep }
      step.number = this.task.steps ? this.task.steps.length + 1 : 1
      const task = { ...this.task }
      task.steps.push(step)
      this.$set(this, 'task', task)
    },
    stepWasUpdated(step) {
      const steps = this.task.steps.filter((s) => {
        if (s.number === step.number) {
          return step
        }
        return s
      })
      const task = { ...this.task }
      task.steps = steps
      this.$set(this, 'task', task)
    },
    stepWasDeleted(stepNumber) {
      this.$log.debug('stepWasDeleted', stepNumber)
      const steps = this.task.steps.filter((s) => {
        if (s && s.number !== stepNumber) {
          return s
        }
      })
      const task = { ...this.task }
      task.steps = steps
      this.$set(this, 'task', task)
    },
    imageSelected(e) {
      this.task.image = e
    },
  },
}
</script>
